import { useMemo } from "react";
import { useFirestore } from "hooks/firestore/v2/useFirestore";
import { bettingEvent } from "hooks/firestore/v2/models/bettingEvent";
import {
  filterBettingMarkets,
  processBettingEvent,
  processRacingEvent,
} from "./shared";
import { useCampaigns } from "hooks/firestore/v2/user/useCampaigns";
import { useTokens } from "hooks/firestore/v2/user/useTokens";
import { useHasPermission, usePermissions } from "hooks/useHasPermission";
import { racingEvent } from "hooks/firestore/v2/models/racingEvent";
import { where, type QueryConstraint } from "firebase/firestore";
import { type Market, market } from "hooks/firestore/v2/models/market";
import { bettingCompetitor } from "hooks/firestore/v2/models/bettingCompetitor";
import { racingCompetitor } from "hooks/firestore/v2/models/racingCompetitor";
import type { Always } from "types/utilities";
import { shouldShowPromoBanner } from "utilities/sharedBettingUtilities";
import { parseISO } from "date-fns";

export enum AllowedSelectionTypesEnum {
  SameEventMulti = "SameEventMulti",
  SingleOutcome = "SingleOutcome",
}

export type MarketType = "all" | "sem";

type EventType = "betting" | "racing";

const getMarketsQuery = (marketFilterType: MarketType) => {
  const query: QueryConstraint[] = [];

  if (marketFilterType === "sem") {
    query.push(
      where(
        "allowedSelectionTypes",
        "array-contains",
        AllowedSelectionTypesEnum.SameEventMulti,
      ),
    );
  } else {
    query.push(
      where(
        "allowedSelectionTypes",
        "array-contains",
        AllowedSelectionTypesEnum.SingleOutcome,
      ),
    );
  }

  return query;
};

const isVisibleOnTab = (marketFilterType: MarketType, market: Market) => {
  if (!market) return false;
  if (marketFilterType === "sem") {
    return market?.allowedSelectionTypes?.includes(
      AllowedSelectionTypesEnum.SameEventMulti,
    );
  }
  return true;
};

const useBettingEvent = (eventId: string) => {
  const { codes, isLoading: codesLoading } = useCampaigns();
  const permissions = usePermissions();
  const { tokens } = useTokens();

  const { data, error, isLoading } = useFirestore(
    `/bettingEvents/${eventId}`,
    bettingEvent,
    {
      keyPath: "id",
    },
  );

  const processedEvent = useMemo(() => {
    if (!data) return;

    return processBettingEvent(data, codes, tokens, permissions);
  }, [data]);

  return {
    isLoading: isLoading || codesLoading,
    error,
    event: processedEvent,
  };
};

const useRacingEvent = (eventId: string) => {
  const { codes, isLoading: codesLoading } = useCampaigns();
  const permissions = usePermissions();
  const { tokens } = useTokens();

  const { data, error, isLoading } = useFirestore(
    `/racingEvents/${eventId}`,
    racingEvent,
    {
      keyPath: "id",
    },
  );

  const processedEvent = useMemo(() => {
    if (!data) return;

    return processRacingEvent(data, codes, tokens, permissions);
  }, [data]);

  return {
    isLoading: isLoading || codesLoading,
    error,
    event: processedEvent,
  };
};

const useEventMarkets = (
  eventId: string,
  marketTypes: MarketType,
  eventType: EventType,
) => {
  const {
    data,
    error,
    isLoading: marketsLoading,
  } = useFirestore(
    `/${eventType === "betting" ? "bettingEvents" : "racingEvents"}/${eventId}/markets`,
    market.array(),
    {
      keyPath: "id",
      query: getMarketsQuery(marketTypes),
    },
  );

  const permissions = usePermissions();
  const { codes, isLoading: codesLoading } = useCampaigns();
  const { tokens, isLoading: tokensLoading } = useTokens();

  const isLoading = marketsLoading || codesLoading || tokensLoading;

  const markets = useMemo(() => {
    if (isLoading || !data) return;

    return data.map((market) => {
      const promotionCampaigns =
        market.promotionCampaigns?.filter((campaign) =>
          shouldShowPromoBanner(campaign, codes, tokens, permissions),
        ) || [];

      const canViewPromo = permissions["viewPromotion"] === "GRANTED";

      return {
        ...market,
        attributes: {
          ...market.attributes,
          // casting string booleans into real booleans
          multiable: market?.attributes?.multiable !== "false",
          promotionCashDisabled:
            market?.attributes?.promotionCashDisabled === "true",
          custom: !!market?.attributes?.custom,
        },
        nextBetStop:
          typeof market.nextBetStop === "string"
            ? parseISO(market.nextBetStop)
            : typeof market.nextBetStop?.toDate === "function"
              ? market.nextBetStop.toDate()
              : null,
        promotionCampaigns: canViewPromo ? promotionCampaigns : [],
        showPromoBanner:
          canViewPromo &&
          (market.hasPromotionalMarkets || promotionCampaigns.length > 0),
      };
    });
  }, [isLoading, data, codes, tokens, permissions]);

  return {
    markets,
    error,
    isLoading,
  };
};

const useBettingEventCompetitors = (eventId: string) => {
  const { data, error, isLoading } = useFirestore(
    `/bettingEvents/${eventId}/competitorsV2`,
    bettingCompetitor.array(),
    {
      keyPath: "id",
    },
  );

  return {
    competitors: data,
    error,
    isLoading,
  };
};

const useRacingEventCompetitors = (eventId: string) => {
  const { data, error, isLoading } = useFirestore(
    `/racingEvents/${eventId}/competitors`,
    racingCompetitor.array(),
    {
      keyPath: "id",
    },
  );

  return {
    competitors: data,
    error,
    isLoading,
  };
};

const decorateOutcomes = (
  outcomes: Market["outcomes"],
  event: ProcessedBettingEvent,
) =>
  Object.entries(outcomes).reduce<Market["outcomes"]>(
    (decoratedOutcomes, [outcomeId, outcome]) => {
      if (
        outcome.type.includes("CORRECT_SCORE") &&
        outcome.competitorId &&
        event.competitors
      ) {
        // we need to add competitors to correct score outcomes
        const competitor = event.competitors[outcome.competitorId];

        if (!competitor) return decoratedOutcomes;

        decoratedOutcomes[outcomeId] = {
          ...outcome,
          name: `${competitor.name} - ${outcome.name}`,
        };

        return decoratedOutcomes;
      }

      if (
        ["PLAYER_AWAY", "PLAYER_HOME"].some((type) =>
          outcome.type.includes(type),
        ) &&
        event.mainMarket
      ) {
        // if our outcome is away or home type, we can override
        // competitor id to get avatar/abbreviations from main market
        // this allows us to render it nicely despite the fact that it might not have
        // its own avatar/name setup
        const mainMarketType = outcome.type.includes("PLAYER_AWAY")
          ? "FRANCHISE_AWAY"
          : "FRANCHISE_HOME";
        const mainMarketOutcomeCompetitorId = Object.values(
          event.mainMarket.outcomes,
        ).find((market) => market.type === mainMarketType)?.competitorId;

        decoratedOutcomes[outcomeId] = {
          ...outcome,
          competitorId: mainMarketOutcomeCompetitorId ?? outcome.competitorId,
        };

        return decoratedOutcomes;
      }

      decoratedOutcomes[outcomeId] = outcome;

      return decoratedOutcomes;
    },
    {},
  );

const useBettingEventWithMarkets = (
  eventId: string,
  marketTypes: MarketType,
) => {
  const canViewLiveBets = useHasPermission("viewLiveMarkets");
  const { event, isLoading: eventLoading } = useBettingEvent(eventId);
  const { codes, isLoading: codesLoading } = useCampaigns();
  const permissions = usePermissions();

  const { markets, isLoading: marketsLoading } = useEventMarkets(
    eventId,
    marketTypes,
    "betting",
  );

  const { competitors, isLoading: competitorsLoading } =
    useBettingEventCompetitors(eventId);

  const isLoading =
    eventLoading || marketsLoading || competitorsLoading || codesLoading;

  const eventWithMarkets = useMemo(() => {
    if (isLoading || !event || !competitors) return;

    return {
      ...event,
      competitors: Object.fromEntries(
        competitors.map((competitor) => [competitor.id, competitor]),
      ),
      markets: (markets || [])
        .filter((market) => filterBettingMarkets(market, permissions, event))
        .map((market) => ({
          ...market,
          outcomes: decorateOutcomes(market.outcomes, event),
        })),
    };
  }, [
    isLoading,
    racingEvent,
    markets,
    canViewLiveBets,
    event,
    permissions,
    competitors,
    codes,
  ]);

  return {
    event: eventWithMarkets,
    isLoading,
  };
};

const useRacingEventWithMarkets = (
  eventId: string,
  marketTypes: MarketType,
) => {
  const canViewLiveBets = useHasPermission("viewLiveMarkets");
  const { event, isLoading: eventLoading } = useRacingEvent(eventId);
  const { codes, isLoading: codesLoading } = useCampaigns();
  const permissions = usePermissions();

  const { markets, isLoading: marketsLoading } = useEventMarkets(
    eventId,
    marketTypes,
    "racing",
  );

  const { competitors, isLoading: competitorsLoading } =
    useRacingEventCompetitors(eventId);

  const isLoading =
    eventLoading || marketsLoading || competitorsLoading || codesLoading;

  const eventWithMarkets = useMemo(() => {
    if (isLoading || !event || !markets || !competitors) return;

    return {
      ...event,
      hub: "racing",
      competitors: Object.fromEntries(
        competitors.map((competitor) => [competitor.id, competitor]),
      ),
      markets,
    };
  }, [
    isLoading,
    racingEvent,
    markets,
    canViewLiveBets,
    event,
    permissions,
    competitors,
    codes,
  ]);

  return {
    event: eventWithMarkets,
    isLoading,
  };
};

const useEvent = (eventId: string, marketTypes: MarketType) => {
  const { event: racingEvent, isLoading: racingEventLoading } =
    useRacingEventWithMarkets(eventId, marketTypes);
  const { event: bettingEvent, isLoading: bettingEventLoading } =
    useBettingEventWithMarkets(eventId, marketTypes);

  const event = racingEvent ?? bettingEvent;

  return {
    event,
    isLoading: racingEventLoading || bettingEventLoading,
  };
};

type ProcessedBettingEvent = Always<
  ReturnType<typeof useBettingEvent>["event"]
>;

type BettingEventWithMarkets = Always<
  ReturnType<typeof useBettingEventWithMarkets>["event"]
>;

type ProcessedMarkets = Always<ReturnType<typeof useEventMarkets>["markets"]>;

export { useEvent, useBettingEventWithMarkets, isVisibleOnTab };
export type {
  BettingEventWithMarkets,
  ProcessedMarkets,
  ProcessedBettingEvent,
};
